import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import BrandLogo from "../components/BrandLogo";

const BRAND = "Zaya Life";
const TAGLINE = "Abu Dhabi Surrogacy Agency";

function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.unobserve(el); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function FadeIn({ children, delay = 0, style = {} }) {
  const [ref, inView] = useInView(0.08);
  return (
    <div
      ref={ref}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(28px)",
        transition: `opacity 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}s, transform 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

const C = {
  bg: "#FDFBF7",
  bgWarm: "#F8F4ED",
  bgSoft: "#F2EDE4",
  text: "#2C2520",
  textMed: "#5C5347",
  textLight: "#8C8278",
  textFaint: "#B0A89E",
  sage: "#7A9B6D",
  sageDark: "#5C7D50",
  sageLight: "#E8F0E4",
  sageBg: "#F3F7F1",
  gold: "#C4A265",
  goldLight: "#F5EFE0",
  border: "#E8E2D8",
  borderLight: "#F0EBE3",
};

const font = {
  display: "'Merriweather', Georgia, serif",
  body: "'Inter', 'Helvetica Neue', sans-serif",
};

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return (
    <nav style={{
      position: "fixed", top: "var(--banner-h, 0px)", left: 0, right: 0, zIndex: 100,
      background: scrolled ? "rgba(253,251,247,0.95)" : "transparent",
      backdropFilter: scrolled ? "blur(16px)" : "none",
      borderBottom: scrolled ? `1px solid ${C.borderLight}` : "1px solid transparent",
      transition: "all 0.4s ease",
      padding: "0 clamp(20px, 5vw, 72px)",
    }}>
      <div style={{
        maxWidth: 1140, margin: "0 auto", display: "flex",
        alignItems: "center", justifyContent: "space-between",
        height: scrolled ? 110 : 132, transition: "height 0.4s ease",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Link to="/" style={{ textDecoration: "none", lineHeight: 0 }}>
            <BrandLogo iconSize={114} textSize={24} textColor={C.sageDark} />
          </Link>
          <span className="nav-tagline" style={{ fontSize: 10, color: C.textFaint, letterSpacing: "0.18em", textTransform: "uppercase", fontFamily: font.body, fontWeight: 400 }}>{TAGLINE}</span>
        </div>
        <div className="nav-links" style={{ display: "flex", alignItems: "center", gap: 36 }}>
          {[
            { name: "Home", path: "/" },
            { name: "For Parents", path: "/parents" },
            { name: "Programs", path: "/programs" }
          ].map(item => (
            <Link key={item.name} to={item.path} style={{
              color: C.textMed, fontSize: 14, textDecoration: "none",
              fontFamily: font.body, fontWeight: 400, transition: "color 0.3s",
            }}
              onMouseEnter={e => e.target.style.color = C.text}
              onMouseLeave={e => e.target.style.color = C.textMed}
            >{item.name}</Link>
          ))}
          <a href="#consultation" className="nav-cta" style={{
            color: "#fff", background: C.sage, padding: "10px 26px",
            fontSize: 13, fontFamily: font.body, fontWeight: 600,
            textDecoration: "none", borderRadius: 6, transition: "all 0.3s",
          }}
            onMouseEnter={e => e.target.style.background = C.sageDark}
            onMouseLeave={e => e.target.style.background = C.sage}
          >Register Your Interest</a>
        </div>
        <button
          className="nav-mobile-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          style={{ display: "none" }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={C.text} strokeWidth="2" strokeLinecap="round">
            {mobileMenuOpen ? (
              <>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </>
            ) : (
              <>
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </>
            )}
          </svg>
        </button>
      </div>
      {mobileMenuOpen && (
        <div className="nav-mobile-menu" style={{
          position: "absolute", top: "100%", left: 0, right: 0,
          background: "rgba(253,251,247,0.98)", backdropFilter: "blur(16px)",
          borderBottom: `1px solid ${C.borderLight}`, padding: "20px",
        }}>
          {[
            { name: "Home", path: "/" },
            { name: "For Parents", path: "/parents" },
            { name: "Programs", path: "/programs" }
          ].map(item => (
            <Link key={item.name} to={item.path} style={{
              display: "block", color: C.textMed, fontSize: 16, textDecoration: "none",
              fontFamily: font.body, fontWeight: 400, padding: "12px 0",
              borderBottom: `1px solid ${C.borderLight}`,
            }}
              onClick={() => setMobileMenuOpen(false)}
            >{item.name}</Link>
          ))}
          <a href="#consultation" style={{
            display: "block", color: "#fff", background: C.sage, padding: "14px 26px",
            fontSize: 15, fontFamily: font.body, fontWeight: 600, textAlign: "center",
            textDecoration: "none", borderRadius: 6, marginTop: "16px",
          }}
            onClick={() => setMobileMenuOpen(false)}
          >Register Your Interest</a>
        </div>
      )}
    </nav>
  );
}

function Hero() {
  return (
    <section className="hero-section" style={{
      minHeight: "100vh", display: "flex", alignItems: "center",
      padding: "120px clamp(20px, 5vw, 72px) 80px",
      background: `linear-gradient(160deg, ${C.bg} 0%, ${C.bgWarm} 50%, ${C.bgSoft} 100%)`,
      position: "relative", overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", top: -120, right: -100, width: 500, height: 500,
        borderRadius: "50%", background: `radial-gradient(circle, ${C.sageLight} 0%, transparent 70%)`, opacity: 0.5,
      }} />
      <div style={{
        position: "absolute", bottom: -80, left: -60, width: 400, height: 400,
        borderRadius: "50%", background: `radial-gradient(circle, ${C.goldLight} 0%, transparent 70%)`, opacity: 0.4,
      }} />

      <div className="grid-hero" style={{ maxWidth: 1140, margin: "0 auto", width: "100%", display: "grid", gridTemplateColumns: "1fr 440px", gap: 64, alignItems: "center", position: "relative" }}>
        <div className="hero-copy" style={{ textAlign: "left", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <FadeIn>
            <div className="hero-badge" style={{
              display: "inline-flex", alignItems: "center", gap: 10,
              padding: "7px 16px", background: C.sageBg,
              borderRadius: 100, marginBottom: 36, border: `1px solid ${C.sageLight}`,
            }}>
              <div style={{ width: 7, height: 7, background: C.sage, borderRadius: "50%" }} />
              <span style={{ fontSize: 12, color: C.sageDark, fontFamily: font.body, fontWeight: 500 }}>Surrogacy coordination in Abu Dhabi</span>
            </div>
          </FadeIn>

          <FadeIn delay={0.12}>
            <h1 className="responsive-h1 homepage-hero-title" style={{
              fontFamily: font.display, fontSize: "clamp(38px, 5vw, 64px)",
              color: C.text, fontWeight: 600, lineHeight: 1.1,
              margin: "0 0 24px", letterSpacing: "-0.02em",
            }}>
              Your child, born<br />in Abu Dhabi
            </h1>
          </FadeIn>

          <FadeIn delay={0.24}>
            <p className="responsive-text-lg homepage-hero-body" style={{
              fontSize: 18, color: C.textMed, maxWidth: 520,
              lineHeight: 1.75, margin: "0 0 16px", fontFamily: font.body, fontWeight: 400,
            }}>
              Professional surrogacy coordination in the UAE with global carrier matching. World-class medical care at an accredited
              Abu Dhabi facility. Complete privacy and support from first call
              to birth certificate.
            </p>
          </FadeIn>

          <FadeIn delay={0.36}>
            <p className="homepage-hero-body" style={{
              fontSize: 14, color: C.textLight, maxWidth: 460,
              lineHeight: 1.65, margin: "0 0 40px", fontFamily: font.body,
            }}>
              For married couples with medical need — under Federal Decree-Law No. 17 of 2023.
            </p>
          </FadeIn>

          <FadeIn delay={0.48}>
            <div className="btn-group homepage-hero-actions" style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <a href="#consultation" style={{
                padding: "14px 32px", background: C.sage, color: "#fff",
                textDecoration: "none", fontSize: 15, fontFamily: font.body,
                fontWeight: 600, borderRadius: 6, transition: "all 0.3s",
              }}
                onMouseEnter={e => e.target.style.background = C.sageDark}
                onMouseLeave={e => e.target.style.background = C.sage}
              >Register Your Interest</a>
              <a href="#process" style={{
                padding: "14px 32px", border: `1.5px solid ${C.border}`,
                color: C.textMed, textDecoration: "none", fontSize: 15,
                fontFamily: font.body, fontWeight: 500, borderRadius: 6,
                transition: "all 0.3s", background: "transparent",
              }}
                onMouseEnter={e => { e.target.style.borderColor = C.textLight; e.target.style.color = C.text; }}
                onMouseLeave={e => { e.target.style.borderColor = C.border; e.target.style.color = C.textMed; }}
              >How It Works</a>
            </div>
          </FadeIn>
        </div>

        <FadeIn delay={0.25}>
          <div className="hero-image-wrapper" style={{ position: "relative" }}>
            {/* Decorative frame accent */}
            <div style={{
              position: "absolute", top: -12, right: -12, width: "100%", height: "100%",
              borderRadius: 24, border: `2px solid ${C.sageLight}`, zIndex: 0,
            }} />
            <div style={{
              position: "relative", zIndex: 1, borderRadius: 20, overflow: "hidden",
              boxShadow: "0 8px 48px rgba(44,37,32,0.1)",
              aspectRatio: "3/4",
            }}>
              <img
                src="https://images.unsplash.com/photo-1544126592-807ade215a0b?w=800&h=1000&fit=crop&crop=faces"
                alt="Happy pregnant woman"
                style={{
                  width: "100%", height: "100%", objectFit: "cover", display: "block",
                }}
              />
              {/* Soft gradient overlay at bottom for blending */}
              <div style={{
                position: "absolute", bottom: 0, left: 0, right: 0, height: "30%",
                background: `linear-gradient(to top, ${C.bgWarm}44, transparent)`,
              }} />
            </div>
            {/* Small floating badge on the image */}
            <div style={{
              position: "absolute", bottom: 24, left: 24, zIndex: 2,
              background: "rgba(255,255,255,0.92)", backdropFilter: "blur(12px)",
              padding: "12px 20px", borderRadius: 12,
              boxShadow: "0 2px 16px rgba(44,37,32,0.08)",
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8, background: C.sageBg,
                border: `1px solid ${C.sageLight}`, display: "flex",
                alignItems: "center", justifyContent: "center",
              }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.sage }} />
              </div>
              <div>
                <div style={{ fontSize: 13, color: C.text, fontFamily: font.body, fontWeight: 600, lineHeight: 1.3 }}>Based in Abu Dhabi</div>
                <div style={{ fontSize: 11, color: C.textLight, fontFamily: font.body }}>United Arab Emirates</div>
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

function StatBar() {
  const stats = [
    { value: "2023", sub: "Federal Decree-Law" },
    { value: "13", sub: "Source Countries" },
    { value: "2", sub: "Service Tiers" },
  ];
  return (
    <section className="section-padding" style={{ background: "#fff", padding: "44px clamp(20px, 5vw, 72px)", borderBottom: `1px solid ${C.borderLight}` }}>
      <div className="grid-3-cols" style={{ maxWidth: 960, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 }}>
        {stats.map((s, i) => (
          <FadeIn key={i} delay={i * 0.08}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontFamily: font.display, fontSize: 30, color: C.sage, fontWeight: 600 }}>{s.value}</div>
              <div style={{ fontSize: 12, color: C.textLight, marginTop: 4, fontFamily: font.body }}>{s.sub}</div>
            </div>
          </FadeIn>
        ))}
      </div>
    </section>
  );
}

function WhyAbuDhabi() {
  const points = [
    { title: "Gestational only", desc: "The carrier has zero genetic connection to your child — using your embryo and your DNA exclusively." },
    { title: "For eligible couples", desc: "Available to married couples with medical need, approved by the DoH Medical Committee." },
    { title: "Your names on the certificate", desc: "Parental rights established at birth — not through a post-birth legal process." },
    { title: "Fully regulated care", desc: "Every procedure at a DoH-accredited facility with comprehensive legal safeguards in place." },
  ];
  return (
    <section className="section-padding-lg" style={{ padding: "100px clamp(20px, 5vw, 72px)", background: C.bg }}>
      <div style={{ maxWidth: 1140, margin: "0 auto" }}>
        <FadeIn>
          <div style={{ fontSize: 12, color: C.sage, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 14, fontFamily: font.body, fontWeight: 600 }}>Why Abu Dhabi</div>
        </FadeIn>
        <FadeIn delay={0.08}>
          <h2 className="responsive-h2" style={{
            fontFamily: font.display, fontSize: "clamp(28px, 3.5vw, 42px)",
            color: C.text, fontWeight: 600, lineHeight: 1.2,
            margin: "0 0 20px",
          }}>
            The only regulated surrogacy framework in the Gulf
          </h2>
        </FadeIn>
        <FadeIn delay={0.16}>
          <p className="responsive-text-lg" style={{
            fontSize: 17, color: C.textMed, lineHeight: 1.75,
            maxWidth: "60%", margin: "0 0 56px", fontFamily: font.body,
          }}>
            In 2023, the UAE became the first country in the region to establish
            a legal framework for gestational surrogacy. Your child is born
            at a Department of Health–licensed hospital, with your names appearing
            on the birth certificate from day one. This establishes direct parental
            rights with complete legal clarity.
          </p>
        </FadeIn>

        <div className="grid-2-cols" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          {points.map((p, i) => (
            <FadeIn key={i} delay={0.1 + i * 0.08}>
              <div style={{
                padding: 32, background: "#fff", borderRadius: 12,
                border: `1px solid ${C.borderLight}`, transition: "box-shadow 0.3s, border-color 0.3s",
              }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 20px rgba(44,37,32,0.06)"; e.currentTarget.style.borderColor = C.border; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = C.borderLight; }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: 8, background: C.sageBg,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  marginBottom: 16, border: `1px solid ${C.sageLight}`,
                }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.sage }} />
                </div>
                <div style={{ fontSize: 16, color: C.text, fontFamily: font.body, fontWeight: 600, marginBottom: 8 }}>{p.title}</div>
                <div style={{ fontSize: 14, color: C.textLight, lineHeight: 1.65, fontFamily: font.body }}>{p.desc}</div>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

function Process() {
  const steps = [
    { num: "01", title: "Apply & qualify", desc: "You apply through our portal, and our partner IVF center confirms eligibility under DoH guidelines — married couple with medical need, using your own genetic material. The DoH Medical Committee then reviews and approves your application.", time: "2–6 weeks" },
    { num: "02", title: "Browse & match", desc: "Access our global carrier database to view verified profiles including medical history, video introductions, and personal bios. Once you select your carrier, we arrange a video introduction. No 12-month waitlist as carriers are available immediately.", time: "1–4 weeks" },
    { num: "03", title: "Screen & prepare", desc: "After your matched carrier completes final medical screening in her home country, we coordinate her employment visa, travel, and arrival in Abu Dhabi for full medical onboarding at the IVF center.", time: "6–10 weeks" },
    { num: "04", title: "Legal agreement", desc: "A notarized tripartite contract is established between you, the carrier, and the agency, with parental rights, responsibilities, and liability all defined before embryo transfer. Your IVF center contract remains separate.", time: "1 week" },
    { num: "05", title: "Transfer & confirm", desc: "A single tested embryo transfer takes place at the accredited Abu Dhabi facility, with pregnancy confirmation approximately 12 days post-transfer. We continuously send you updates at every stage.", time: "3–4 weeks" },
    { num: "06", title: "Pregnancy & birth", desc: "Full prenatal care is provided at the IVF center with regular updates throughout the pregnancy. Delivery takes place at a DoH-licensed hospital with you present, and the birth certificate is issued in your names.", time: "~9 months" },
  ];
  return (
    <section id="process" style={{ padding: "100px clamp(20px, 5vw, 72px)", background: "#fff" }}>
      <div style={{ maxWidth: 1140, margin: "0 auto" }}>
        <FadeIn>
          <div style={{ fontSize: 12, color: C.sage, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 14, fontFamily: font.body, fontWeight: 600 }}>The process</div>
        </FadeIn>
        <FadeIn delay={0.08}>
          <h2 style={{
            fontFamily: font.display, fontSize: "clamp(28px, 3.5vw, 42px)",
            color: C.text, fontWeight: 600, lineHeight: 1.2,
            margin: "0 0 12px",
          }}>
            Six stages with one point of contact
          </h2>
        </FadeIn>
        <FadeIn delay={0.16}>
          <p className="responsive-description" style={{ fontSize: 16, color: C.textLight, lineHeight: 1.65, margin: "0 0 56px", fontFamily: font.body, maxWidth: "60%" }}>
            All medical procedures take place in Abu Dhabi at a single accredited facility, where you contract directly with the IVF center for medical care while we handle everything else.
          </p>
        </FadeIn>

        {steps.map((step, i) => (
          <FadeIn key={i} delay={0.06 * i}>
            <div className="step-grid" style={{
              display: "grid", gridTemplateColumns: "56px 1fr 90px",
              gap: 28, padding: "32px 0",
              borderTop: `1px solid ${C.borderLight}`, alignItems: "start",
            }}>
              <div style={{
                fontFamily: font.display, fontSize: 26, color: C.sage, fontWeight: 600, opacity: 0.5,
              }}>{step.num}</div>
              <div>
                <div style={{ fontSize: 17, color: C.text, fontFamily: font.body, fontWeight: 600, marginBottom: 8 }}>{step.title}</div>
                <div style={{ fontSize: 15, color: C.textMed, lineHeight: 1.7, fontFamily: font.body, maxWidth: 560 }}>{step.desc}</div>
              </div>
              <div className="step-time" style={{ fontSize: 13, color: C.textFaint, fontFamily: font.body, textAlign: "right", paddingTop: 2, fontWeight: 500 }}>{step.time}</div>
            </div>
          </FadeIn>
        ))}
      </div>
    </section>
  );
}

function Tiers() {
  const CheckIcon = ({ filled }) => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="8" cy="8" r="7" fill={filled ? C.sageLight : "none"} stroke={filled ? C.sage : C.border} strokeWidth="1.5" />
      <path d="M5.5 8L7.2 9.7L10.5 6.3" stroke={C.sage} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  return (
    <section className="section-padding-lg" style={{ padding: "100px clamp(20px, 5vw, 72px)", background: C.bgWarm }}>
      <div style={{ maxWidth: 1140, margin: "0 auto" }}>
        <FadeIn>
          <div style={{ fontSize: 12, color: C.sage, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 14, fontFamily: font.body, fontWeight: 600 }}>Two programs</div>
        </FadeIn>
        <FadeIn delay={0.08}>
          <h2 className="responsive-h2" style={{
            fontFamily: font.display, fontSize: "clamp(28px, 3.5vw, 42px)",
            color: C.text, fontWeight: 600, lineHeight: 1.2,
            margin: "0 0 56px",
          }}>
            Choose the level of involvement that fits your situation
          </h2>
        </FadeIn>

        <div className="grid-2-cols" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          <FadeIn delay={0.15}>
            <div style={{
              padding: 44, background: "#fff", borderRadius: 16,
              border: `1px solid ${C.borderLight}`, height: "100%",
              display: "flex", flexDirection: "column",
            }}>
              <div style={{ fontSize: 12, color: C.textFaint, fontFamily: font.body, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>Tier 1</div>
              <h3 className="responsive-h3" style={{ fontFamily: font.display, fontSize: 28, color: C.text, fontWeight: 600, margin: "0 0 28px" }}>Matching</h3>
              <div style={{ flex: 1 }}>
                {["Global carrier sourcing and matching", "Medical and psychological pre-screening", "Visa coordination and travel to Abu Dhabi", "IVF center introduction and onboarding", "Legal contract coordination"].map((item, i) => (
                  <div key={i} style={{ padding: "11px 0", borderBottom: `1px solid ${C.borderLight}`, fontSize: 14, color: C.textMed, fontFamily: font.body, display: "flex", alignItems: "center", gap: 12 }}>
                    <CheckIcon />{item}
                  </div>
                ))}
              </div>
              <p style={{ fontSize: 13, color: C.textLight, lineHeight: 1.6, margin: "24px 0 0", fontFamily: font.body }}>
                Post-arrival, you coordinate directly with the IVF center. Ideal for parents comfortable managing their own journey with clinical support.
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.25}>
            <div style={{
              padding: 44, background: "#fff", borderRadius: 16,
              border: `2px solid ${C.sage}`, height: "100%",
              display: "flex", flexDirection: "column",
              position: "relative", boxShadow: `0 8px 40px rgba(122,155,109,0.1)`,
            }}>
              <div style={{
                position: "absolute", top: -1, right: 28,
                padding: "6px 16px", background: C.sage, color: "#fff",
                fontSize: 11, fontWeight: 600, fontFamily: font.body,
                borderRadius: "0 0 8px 8px",
              }}>Recommended</div>
              <div style={{ fontSize: 12, color: C.textFaint, fontFamily: font.body, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>Tier 2</div>
              <h3 className="responsive-h3" style={{ fontFamily: font.display, fontSize: 28, color: C.text, fontWeight: 600, margin: "0 0 28px" }}>White Glove</h3>
              <div style={{ flex: 1 }}>
                {[
                  { text: "Includes Everything in Matching Plus", bold: true },
                  { text: "Full concierge coordination through birth" },
                  { text: "Agency-managed carrier housing in Abu Dhabi" },
                  { text: "Ongoing care and welfare management" },
                  { text: "Dedicated coordinator — single point of contact" },
                  { text: "Prenatal appointment coordination" },
                  { text: "Birth logistics and post-birth support" },
                ].map((item, i) => (
                  <div key={i} style={{ padding: "11px 0", borderBottom: `1px solid ${C.borderLight}`, fontSize: 14, color: item.bold ? C.text : C.textMed, fontFamily: font.body, fontWeight: item.bold ? 600 : 400, display: "flex", alignItems: "center", gap: 12 }}>
                    {!item.bold ? <CheckIcon filled /> : <div style={{ width: 16 }} />}
                    {item.text}
                  </div>
                ))}
              </div>
              <p style={{ fontSize: 13, color: C.textLight, lineHeight: 1.6, margin: "24px 0 0", fontFamily: font.body }}>
                You attend the key moments while we handle everything in between—coordinating all aspects of your journey from start to finish.
              </p>
            </div>
          </FadeIn>
        </div>

        <FadeIn delay={0.4}>
          <p style={{ fontSize: 13, color: C.textFaint, textAlign: "center", marginTop: 28, fontFamily: font.body }}>
            Both programs: medical care is contracted directly between you and the IVF center, while we coordinate matching, screening, and all logistics.
          </p>
        </FadeIn>
      </div>
    </section>
  );
}

function GlobalMatching() {
  const criteria = [
    { label: "Age", value: "21 – 45" }, { label: "BMI range", value: "19 – 30" },
    { label: "Prior births", value: "Minimum one" }, { label: "Maximum births", value: "Four" },
    { label: "Maximum C-sections", value: "Two" }, { label: "Chronic conditions", value: "None" },
    { label: "Psychological evaluation", value: "Required" }, { label: "Background check", value: "Required" },
  ];
  return (
    <section className="section-padding-lg" style={{ padding: "100px clamp(20px, 5vw, 72px)", background: C.bg }}>
      <div className="grid-2-cols" style={{ maxWidth: 1140, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "start" }}>
        <div>
          <FadeIn>
            <div style={{ fontSize: 12, color: C.sage, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 14, fontFamily: font.body, fontWeight: 600 }}>Global carrier network</div>
          </FadeIn>
          <FadeIn delay={0.08}>
            <h2 className="responsive-h2" style={{ fontFamily: font.display, fontSize: "clamp(28px, 3.5vw, 42px)", color: C.text, fontWeight: 600, lineHeight: 1.2, margin: "0 0 24px" }}>
              No waitlist.<br />Choose your carrier directly.
            </h2>
          </FadeIn>
          <FadeIn delay={0.16}>
            <p className="responsive-description" style={{ fontSize: 16, color: C.textMed, lineHeight: 1.75, fontFamily: font.body, margin: "0 0 20px" }}>
              Our global sourcing network has dedicated recruiters across 13 countries,
              pre-screening every candidate against Abu Dhabi Department of Health
              criteria before they enter our database.
            </p>
          </FadeIn>
          <FadeIn delay={0.24}>
            <p style={{ fontSize: 16, color: C.textMed, lineHeight: 1.75, fontFamily: font.body, margin: 0 }}>
              You browse verified profiles with detailed medical history, video introductions,
              and personal bios, then make your choice. All medical procedures happen here in Abu Dhabi
              under UAE law and DoH standards — regardless of where the carrier was recruited.
            </p>
          </FadeIn>
        </div>

        <FadeIn delay={0.15}>
          <div style={{
            padding: 36, background: "#fff", borderRadius: 16,
            border: `1px solid ${C.borderLight}`, boxShadow: "0 2px 20px rgba(44,37,32,0.04)",
          }}>
            <div style={{ fontSize: 13, color: C.text, fontFamily: font.body, fontWeight: 600, marginBottom: 24 }}>Carrier screening criteria</div>
            {criteria.map((item, i) => (
              <div key={i} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "13px 0", borderBottom: i < criteria.length - 1 ? `1px solid ${C.borderLight}` : "none",
              }}>
                <span style={{ fontSize: 14, color: C.textLight, fontFamily: font.body }}>{item.label}</span>
                <span style={{ fontSize: 14, color: C.text, fontFamily: font.body, fontWeight: 600 }}>{item.value}</span>
              </div>
            ))}
            <div style={{
              marginTop: 20, fontSize: 12, color: C.textFaint, lineHeight: 1.6,
              fontFamily: font.body, padding: "12px 16px", background: C.sageBg, borderRadius: 8,
            }}>
              All criteria per Abu Dhabi Department of Health guidelines. Final medical clearance at the partnered IVF center.
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

function Privacy() {
  return (
    <section className="section-padding" style={{ padding: "88px clamp(20px, 5vw, 72px)", background: C.bgSoft }}>
      <div style={{ maxWidth: 640, margin: "0 auto", textAlign: "center" }}>
        <FadeIn>
          <div style={{ width: 48, height: 2, background: C.sage, margin: "0 auto 36px", borderRadius: 1, opacity: 0.4 }} />
        </FadeIn>
        <FadeIn delay={0.08}>
          <h2 className="responsive-h2" style={{
            fontFamily: font.display, fontSize: "clamp(24px, 3vw, 34px)",
            color: C.text, fontWeight: 600, lineHeight: 1.35,
            margin: "0 0 20px", fontStyle: "italic",
          }}>
            We understand this is private.<br />We treat it that way.
          </h2>
        </FadeIn>
        <FadeIn delay={0.16}>
          <p className="responsive-description" style={{ fontSize: 16, color: C.textMed, lineHeight: 1.8, fontFamily: font.body, margin: "0 0 32px" }}>
            Your consultation is confidential and records are secure — no information is shared without your written consent.
            Every communication across all digital and in-person channels are handled
            with the discretion this decision deserves. Your circle does not
            need to know until you decide they should.
          </p>
        </FadeIn>
        <FadeIn delay={0.24}>
          <a href="#consultation" style={{
            display: "inline-block", padding: "13px 32px",
            border: `1.5px solid ${C.sage}`, color: C.sageDark,
            textDecoration: "none", fontSize: 14, fontFamily: font.body,
            fontWeight: 600, borderRadius: 6, transition: "all 0.3s", background: "transparent",
          }}
            onMouseEnter={e => { e.target.style.background = C.sage; e.target.style.color = "#fff"; }}
            onMouseLeave={e => { e.target.style.background = "transparent"; e.target.style.color = C.sageDark; }}
          >Register Your Interest</a>
        </FadeIn>
      </div>
    </section>
  );
}

function ConsultationCTA() {
  const [formState, setFormState] = useState({ submitting: false, succeeded: false, error: false });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormState({ submitting: true, succeeded: false, error: false });

    try {
      const response = await fetch('https://formspree.io/f/xlgororn', {
        method: 'POST',
        body: new FormData(e.target),
        headers: { 'Accept': 'application/json' }
      });

      if (response.ok) {
        setFormState({ submitting: false, succeeded: true, error: false });
        e.target.reset();
      } else {
        setFormState({ submitting: false, succeeded: false, error: true });
      }
    } catch (error) {
      setFormState({ submitting: false, succeeded: false, error: true });
    }
  };

  return (
    <section id="consultation" className="section-padding-lg" style={{ padding: "100px clamp(20px, 5vw, 72px)", background: "#fff" }}>
      <div className="grid-2-cols" style={{ maxWidth: 1000, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 420px", gap: 72, alignItems: "start" }}>
        <div>
          <FadeIn>
            <h2 className="responsive-h2" style={{ fontFamily: font.display, fontSize: "clamp(28px, 3.5vw, 40px)", color: C.text, fontWeight: 600, lineHeight: 1.2, margin: "0 0 20px" }}>
              Your first step is a conversation
            </h2>
          </FadeIn>
          <FadeIn delay={0.08}>
            <p className="responsive-description" style={{ fontSize: 17, color: C.textMed, lineHeight: 1.75, fontFamily: font.body, margin: "0 0 36px" }}>
              A private, 30-minute call where we listen to your situation and explain what the process looks like for you specifically.
            </p>
          </FadeIn>
          <FadeIn delay={0.16}>
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {[
                { label: "Response time", value: "Within 24 hours" },
                { label: "Languages", value: "English and Arabic" },
                { label: "Format", value: "Video call or phone" },
                { label: "What we cover", value: "Your situation, eligibility, timeline, next steps" },
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", gap: 20, alignItems: "baseline" }}>
                  <span style={{ fontSize: 13, color: C.textFaint, fontFamily: font.body, fontWeight: 500, minWidth: 110 }}>{item.label}</span>
                  <span style={{ fontSize: 15, color: C.text, fontFamily: font.body, fontWeight: 500 }}>{item.value}</span>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>

        <FadeIn delay={0.15}>
          <div className="form-container" style={{ padding: 36, background: C.bgWarm, borderRadius: 16, border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 16, color: C.text, fontFamily: font.body, fontWeight: 600, marginBottom: 24 }}>Register your interest</div>

            {formState.succeeded ? (
              <div style={{ padding: 20, background: C.sageBg, border: `1px solid ${C.sageLight}`, borderRadius: 8, textAlign: "center" }}>
                <div style={{ fontSize: 15, color: C.sageDark, fontFamily: font.body, fontWeight: 600, marginBottom: 8 }}>Request received!</div>
                <div style={{ fontSize: 13, color: C.textMed, fontFamily: font.body, lineHeight: 1.6 }}>
                  We'll contact you within 24 hours to arrange your consultation.
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", fontSize: 13, color: C.textLight, marginBottom: 6, fontFamily: font.body, fontWeight: 500 }}>Name</label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="form-input"
                    style={{
                      width: "100%", padding: "11px 14px", background: "#fff",
                      border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
                      fontSize: 14, fontFamily: font.body, outline: "none", boxSizing: "border-box",
                      transition: "border-color 0.2s",
                    }}
                    onFocus={e => e.target.style.borderColor = C.sage}
                    onBlur={e => e.target.style.borderColor = C.border}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", fontSize: 13, color: C.textLight, marginBottom: 6, fontFamily: font.body, fontWeight: 500 }}>Email</label>
                  <input
                    type="email"
                    name="email"
                    required
                    pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                    title="Please enter a valid email address with @"
                    style={{
                      width: "100%", padding: "11px 14px", background: "#fff",
                      border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
                      fontSize: 14, fontFamily: font.body, outline: "none", boxSizing: "border-box",
                      transition: "border-color 0.2s",
                    }}
                    onFocus={e => e.target.style.borderColor = C.sage}
                    onBlur={e => e.target.style.borderColor = C.border}
                    onInvalid={e => e.target.style.borderColor = "#C33"}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", fontSize: 13, color: C.textLight, marginBottom: 6, fontFamily: font.body, fontWeight: 500 }}>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    pattern="\+[0-9]{7,15}"
                    title="Please enter phone number with country code (e.g., +971501234567)"
                    style={{
                      width: "100%", padding: "11px 14px", background: "#fff",
                      border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
                      fontSize: 14, fontFamily: font.body, outline: "none", boxSizing: "border-box",
                      transition: "border-color 0.2s",
                    }}
                    onFocus={e => e.target.style.borderColor = C.sage}
                    onBlur={e => e.target.style.borderColor = C.border}
                    onInvalid={e => e.target.style.borderColor = "#C33"}
                  />
                  <div style={{ fontSize: 11, color: C.textLight, marginTop: 4, fontFamily: font.body }}>
                    Must start with + and country code (e.g., +971 for UAE)
                  </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", fontSize: 13, color: C.textLight, marginBottom: 6, fontFamily: font.body, fontWeight: 500 }}>Preferred contact method</label>
                  <select
                    name="contact_method"
                    style={{
                      width: "100%", padding: "11px 14px", background: "#fff",
                      border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
                      fontSize: 14, fontFamily: font.body, outline: "none", appearance: "none", boxSizing: "border-box",
                    }}
                  >
                    {["Video call", "Phone call"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: "block", fontSize: 13, color: C.textLight, marginBottom: 6, fontFamily: font.body, fontWeight: 500 }}>Anything you'd like us to know</label>
                  <textarea
                    name="message"
                    rows={3}
                    style={{
                      width: "100%", padding: "11px 14px", background: "#fff",
                      border: `1px solid ${C.border}`, borderRadius: 8, color: C.text,
                      fontSize: 14, fontFamily: font.body, outline: "none", resize: "vertical", boxSizing: "border-box",
                    }}
                  />
                </div>

                {formState.error && (
                  <div style={{ padding: 12, background: "#FEE", border: "1px solid #FCC", borderRadius: 6, marginBottom: 16, fontSize: 13, color: "#C33", fontFamily: font.body }}>
                    Something went wrong. Please try again or contact us directly.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formState.submitting}
                  style={{
                    width: "100%", padding: "13px 0", background: formState.submitting ? C.textFaint : C.sage, color: "#fff",
                    border: "none", fontSize: 15, fontFamily: font.body, fontWeight: 600,
                    cursor: formState.submitting ? "not-allowed" : "pointer", borderRadius: 8, transition: "background 0.3s",
                  }}
                  onMouseEnter={e => { if (!formState.submitting) e.target.style.background = C.sageDark }}
                  onMouseLeave={e => { if (!formState.submitting) e.target.style.background = C.sage }}
                >
                  {formState.submitting ? "Sending..." : "Register Your Interest"}
                </button>
                <p style={{ fontSize: 12, color: C.textFaint, marginTop: 14, lineHeight: 1.5, fontFamily: font.body }}>
                  Your information is confidential and will only be used to arrange your consultation.
                </p>
              </form>
            )}
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer style={{ padding: "56px clamp(20px, 5vw, 72px) 36px", background: C.text }}>
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        <div className="footer-grid" style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 44 }}>
          <div>
            <div style={{ fontFamily: font.display, fontSize: 20, color: "#fff", fontWeight: 600, marginBottom: 8 }}>{BRAND}</div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", fontFamily: font.body, lineHeight: 1.6, maxWidth: 260 }}>
              Surrogacy coordination agency.<br />Abu Dhabi, United Arab Emirates.
            </div>
          </div>
          <div style={{ display: "flex", gap: 48 }}>
            <div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14, fontFamily: font.body, fontWeight: 600 }}>Navigate</div>
              {[
                { name: "Home", path: "/" },
                { name: "For Parents", path: "/parents" },
                { name: "Programs", path: "/programs" },
                { name: "FAQ", path: "#" }
              ].map(item => (
                item.path === "#" ? (
                  <button key={item.name} type="button" style={{ display: "block", color: "rgba(255,255,255,0.55)", fontSize: 14, textDecoration: "none", marginBottom: 10, fontFamily: font.body, background: "none", border: "none", padding: 0, cursor: "default" }}>{item.name}</button>
                ) : (
                  <Link key={item.name} to={item.path} style={{ display: "block", color: "rgba(255,255,255,0.55)", fontSize: 14, textDecoration: "none", marginBottom: 10, fontFamily: font.body }}>{item.name}</Link>
                )
              ))}
            </div>
            <div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14, fontFamily: font.body, fontWeight: 600 }}>Contact</div>
              <div style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", fontFamily: font.body }}>info@zayalife.ae</div>
            </div>
          </div>
        </div>
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 20, display: "flex", justifyContent: "space-between" }}>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.25)", fontFamily: font.body }}>© 2026 {BRAND}. All rights reserved.</div>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  useEffect(() => {
    // Handle hash navigation for smooth scrolling from other pages
    if (window.location.hash) {
      setTimeout(() => {
        const id = window.location.hash.substring(1);
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  return (
    <div style={{ background: C.bg, minHeight: "100vh", paddingTop: "var(--banner-h, 0px)" }}>
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;1,400;1,500;1,600&family=Source+Sans+3:wft@300;400;500;600&display=swap" rel="stylesheet" />
      <Nav />
      <Hero />
      <StatBar />
      <WhyAbuDhabi />
      <Process />
      <Tiers />
      <GlobalMatching />
      <Privacy />
      <ConsultationCTA />
      <Footer />
    </div>
  );
}
